import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import trackClick from 'js/utils/trackClick';

import {
  acknowledgeSeenValue,
  dotVisible,
  readSeenValues,
} from '../acknowledgment';
import type { SeenMetric, SeenValues } from '../acknowledgment';
import { impressionExtra } from '../arms';
import type { NavImpactSurfaces, StatLayout, StatStyle } from '../arms';
import type { ImpactStats } from '../../types';
import ImpactStatTile, {
  INLINE_TILE_WIDTH,
  STACKED_TILE_WIDTH,
} from '../ImpactStatTile';
import { trendAdornment } from '../ornaments/TrendAdornment';
import type { TrendMetric } from '../ornaments/TrendAdornment';

const Cell = styled.div<{
  statStyle: StatStyle;
  tint: 'white' | 'beige';
  interactive?: boolean;
  skeleton?: boolean;
}>`
  display: flex;
  flex: 0 0 auto;
  box-sizing: border-box;
  align-items: center;
  height: 100%;

  &.tint-white {
    border-right: 1px solid var(--stroke-light, #dddde2);
  }

  &.tint-beige {
    border-right: 1px solid var(--stroke-beige-light, #ccc9bc);
  }

  &.interactive:hover {
    background: var(--background-light-blue, #eef2f9);
  }

  /* When a tile renders, the gutters live inside the anchor (CellTile below)
     so the whole cell is clickable; the skeleton keeps them on the cell. */
  &.skeleton {
    padding: 0 16px;
  }

  &.skeleton.statStyle-delta_chip {
    align-items: flex-end;
    padding: 0 18px 8px 16px;
  }

  &.skeleton.statStyle-trend_icon {
    padding: 0 16px 0 12px;
  }
`;

const CellTile = styled(ImpactStatTile)<{
  cellStyle?: 'delta_chip' | 'trend_icon';
}>`
  box-sizing: content-box;
  padding: 0 16px;

  &.cellStyle-delta_chip {
    align-items: flex-end;
    padding: 0 18px 8px 16px;
  }

  &.cellStyle-trend_icon {
    padding: 0 16px 0 12px;
  }
`;

const SkeletonStat = styled.span<{ layout: StatLayout }>`
  display: inline-block;
  width: var(--skeleton-width);

  &.layout-stacked {
    height: 39px;
  }

  &.layout-inline {
    height: 16px;
  }
`;

const SKELETON_WIDTHS: Record<StatLayout, number> = {
  stacked: STACKED_TILE_WIDTH,
  inline: INLINE_TILE_WIDTH,
};

const Row = styled.div`
  display: flex;
  flex: 0 0 auto;
  height: 100%;
`;

const RowWatcher = styled(ImpressionWatcher)`
  display: flex;
  flex: 0 0 auto;
  height: 100%;
`;

type CellSpec = {
  key: string;
  count: number | null;
  label: string;
  trendPct: number | null;
  trendMetric: TrendMetric;
  seenMetric: SeenMetric;
  seenValue: number | null;
  href: string;
  source: string;
};

export type StatCellsProps = {
  arm: string | null | undefined;
  surfaces: NavImpactSurfaces;
  impactStats?: ImpactStats | null;
  tint: 'white' | 'beige';
  citationsOnly?: boolean;
  testRunning?: boolean;
};

const StatCells = ({
  arm,
  surfaces,
  impactStats,
  tint,
  citationsOnly = false,
  testRunning = false,
}: StatCellsProps) => {
  const [seenValues, setSeenValues] = useState<SeenValues | null>({});

  useEffect(() => {
    setSeenValues(readSeenValues());
  }, []);

  const bucket = arm ?? 'control';
  const citations = impactStats?.citations ?? null;
  const paperViews = impactStats?.paperViews30d ?? null;
  const paperViewsTotal = impactStats?.paperViewsTotal ?? null;

  const acknowledge = (metric: SeenMetric, value: number | null) => {
    if (value == null) {
      return;
    }
    const merged = acknowledgeSeenValue(metric, value);
    if (merged != null) {
      setSeenValues(merged);
    }
  };

  const paperViewsCell: CellSpec = {
    key: 'paper-views',
    count: paperViews,
    label: 'Paper views',
    trendPct: impactStats?.paperViewsTrendPct ?? null,
    trendMetric: 'views',
    seenMetric: 'paperViewsTotal',
    seenValue: paperViewsTotal,
    href: '/analytics',
    source: 'nav-bar--impact-paper-views',
  };

  const cells: CellSpec[] = [
    {
      key: 'citations',
      count: citations,
      label: 'Citations',
      trendPct: impactStats?.citationsTrendPct ?? null,
      trendMetric: 'citations',
      seenMetric: 'citations',
      seenValue: citations,
      href: routes.citations_url({
        from_navbar: true,
        trigger: 'nav_impact_stats',
      }),
      source: 'nav-bar--impact-citations',
    },
    ...(citationsOnly ? [] : [paperViewsCell]),
  ];

  const hasData = cells.some((cell) => cell.count != null);

  const row = (
    <Row data-testid="impact-stat-cells">
      {cells.map((cell) => (
        <Cell
          key={cell.key}
          statStyle={surfaces.statStyle}
          tint={tint}
          interactive={cell.count != null}
          skeleton={cell.count == null}
          data-testid={`impact-stat-cell-${cell.key}`}
        >
          {cell.count == null ? (
            <SkeletonStat
              layout={surfaces.statLayout}
              style={
                {
                  '--skeleton-width': `${SKELETON_WIDTHS[surfaces.statLayout]}px`,
                } as React.CSSProperties
              }
              data-testid="impact-stat-skeleton"
            />
          ) : (
            <CellTile
              cellStyle={
                surfaces.statStyle === 'delta_chip' ||
                surfaces.statStyle === 'trend_icon'
                  ? surfaces.statStyle
                  : undefined
              }
              count={cell.count}
              label={cell.label}
              layout={surfaces.statLayout}
              statStyle={surfaces.statStyle}
              adornment={trendAdornment(
                surfaces.statStyle,
                cell.trendPct,
                cell.trendMetric,
              )}
              href={cell.href}
              onClick={() => {
                trackClick({ source: cell.source, bucket });
                acknowledge(cell.seenMetric, cell.seenValue);
              }}
              showDot={dotVisible(seenValues, cell.seenMetric, cell.seenValue)}
            />
          )}
        </Cell>
      ))}
    </Row>
  );

  if (!hasData) {
    return row;
  }

  return (
    <RowWatcher
      source="nav_impact_stats.strip"
      extra={impressionExtra(arm, testRunning)}
    >
      {row}
    </RowWatcher>
  );
};

export default StatCells;
